// Section 24.9 and 24.12: zod schemas for anything entering the app from outside (load, import,
// migration output). The GameState schema checks the structure the app relies on; deep entity
// consistency is then checked by the sim's invariants.
import { z } from 'zod';

const finite = z.number();
const idRecord = <T extends z.ZodType>(prefix: string, value: T) =>
  z.record(z.string().regex(new RegExp(`^${prefix}\\d+$`)), value);

const personSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  sex: z.enum(['female', 'male']),
  ageSeasons: z.number().int().nonnegative(),
  health: finite,
  strength: finite,
  happiness: finite,
  trust: finite,
  skills: z.record(z.string(), finite),
  traits: z.array(z.string()),
  householdId: z.string(),
  job: z.string(),
  memory: z.array(z.looseObject({ turn: z.number().int(), kind: z.string() })),
  status: z.looseObject({}),
  streaks: z.looseObject({}),
});

const stockSchema = z.strictObject({ amount: finite, capacity: finite });

export const gameStateSchema = z.looseObject({
  version: z.number().int(),
  seed: z.string(),
  options: z.looseObject({ population: z.number().int() }),
  turn: z.number().int().nonnegative(),
  year: z.number().int().positive(),
  season: z.enum(['spring', 'summer', 'autumn', 'winter']),
  rngState: z.number().int().nonnegative(),
  eventRngState: z.number().int().nonnegative(),
  people: idRecord('p', personSchema),
  households: idRecord('h', z.looseObject({ id: z.string(), memberIds: z.array(z.string()) })),
  buildings: idRecord('b', z.looseObject({ id: z.string(), type: z.string(), condition: finite })),
  buildQueue: z.array(z.looseObject({ id: z.string(), buildingType: z.string() })),
  stocks: z.record(z.string(), stockSchema),
  policies: z.looseObject({}),
  pendingPrompts: z.array(z.looseObject({ kind: z.string(), id: z.string() })),
  openPrompts: z.array(z.looseObject({ kind: z.string(), id: z.string() })),
  phaseCursor: z.string().nullable(),
  history: z.array(
    z.looseObject({
      turn: z.number().int(),
      entries: z.array(z.looseObject({ kind: z.string() })),
    }),
  ),
  series: z.record(z.string(), z.array(finite)),
  decisionLog: z.array(z.looseObject({ turn: z.number().int() })),
  outcome: z.looseObject({ kind: z.enum(['won', 'lost']) }).nullable(),
});

const seasonSchema = z.enum(['spring', 'summer', 'autumn', 'winter']);

export const saveSummarySchema = z.strictObject({
  turn: z.number().int().nonnegative(),
  year: z.number().int().positive(),
  season: seasonSchema,
  population: z.number().int().nonnegative().nullable(),
  outcome: z.enum(['won', 'lost']).nullable(),
  ironman: z.boolean(),
});

export const saveEnvelopeSchema = z.strictObject({
  formatVersion: z.number().int().positive(),
  contentVersion: z.string(),
  configSnapshot: z.looseObject({ meta: z.looseObject({}) }),
  seed: z.string().min(1),
  options: z.looseObject({ population: z.number().int() }),
  decisionLog: z.array(z.looseObject({ turn: z.number().int() })),
  snapshot: gameStateSchema.nullable(),
  ledger: z.looseObject({}).nullable(),
  summary: saveSummarySchema,
  savedAt: z.string(),
});

/** Anything claiming to be an envelope of some format (checked before migrating). */
export const rawEnvelopeSchema = z.looseObject({ formatVersion: z.number().int().positive() });
